import {AppBar, Button, MenuItem, Tab, Tabs, useMediaQuery} from '@material-ui/core';
import {showModal} from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {Loaded, toEither} from 'Core/models/Async';
import AddCustomerSupportMenu, {AddCustomerSupportMobileMenu} from 'CustomerSupport/components/AddCustomerSupportMenu';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import {hasScope} from 'Shared/components/RequireScope';
import withUserClaims, {WithClaimsProps} from 'Shared/components/withUserClaims';
import {Scope} from 'Shared/constants/Scope';
import {hasAccessToScopeByApplication} from 'Shared/utilities/applicationId';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {UserDto} from 'Users/api';
import DeleteUserConfirmationModal from 'Users/components/UserAdministration/DeleteUserConfirmationModal';
import ResetUserPassConfirmationModal from 'Users/components/UserAdministration/ResetUserPasswordConfirmationModal';
import {navigateToEdit} from 'Users/ducks/agentUser';
import {
  changeCustomerSupportFilter,
  CustomerSupportFilter,
  impersonateCustomerSupportUser,
  searchCustomerUsers,
  EmptyCustomerSupportFilter,
} from 'Users/ducks/CustomerSupport';
import {changePreregisteredUsersFilter, EmptyPreregisteredUserFilter} from 'Users/ducks/userAdministration';
import {Application} from 'Users/models/Application';
import {UserClaims} from 'Users/models/Claims';
import {LinesOfBusinessCustomerSupport} from 'Users/models/LinesOfBusinessCustomerSupport';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import {UserModel} from 'Users/models/UserModel';
import {
  selectCurrentIdentifier as selectCurrentEncryptedIdentifier,
  selectUserScopes,
} from 'Users/selectors/userProfileSelectors';
import './CustomerSupport.sass';
import PendingCustomerSupportRegistration from './PendingCustomerSupportRegistration';

export type CustomerSupportProps = {} & WithClaimsProps;
type UserConfirmationState = {userId?: string; username?: string; user?: UserDto};

const CustomerSupport = (props: CustomerSupportProps) => {
  const dispatch = useDispatch();
  const scopes = useSelector(selectUserScopes);
  const userHasScope = hasScope(scopes);
  const {customerSupport} = useSelector((state: ReducerState) => state.user);
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const encryptedTaxId = useSelector(selectCurrentEncryptedIdentifier);
  const changeFilter = withValueChangedHook(customerSupport.filter, changeCustomerSupportFilter, dispatch);
  const {selectedAgency, userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const encryptedIdentifier = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;
  const hasAccessForCustomerSupportEmulate = userHasScope(Scope.SearchAgentUsers) && !O.isSome(selectedAgency);

  const data = toEither(userClaims);
  const claims: UserClaims | undefined = pipe(
    data,
    E.map(value => new Loaded(value)),
    E.fold(
      () => undefined,
      value => value.Data
    )
  )!;

  const recruiterCodes = undefined;

  const filterProperties = {
    changeFilter,
    filterData: customerSupport.filter,
  };
  const match = useRouteMatch();
  const [currentTab, setCurrentTab] = useState(0);
  useBreadcrumbs('Customer Support', match.url);

  const onImpersonate = (userModel: UserModel) =>
    dispatch(
      impersonateCustomerSupportUser(props.claims.userName, userModel.userName!, dispatch, [
        userModel.firstName!,
        userModel.lastName!,
      ])
    );

  const [resetPassConfirmation, setResetPasswordConfirmation] = useState<O.Option<UserConfirmationState>>(O.none);
  const [deleteConfirmation, setDeleteConfirmation] = useState<O.Option<UserConfirmationState>>(O.none);
  const hasAccessForPendingRegestration = hasAccessToScopeByApplication(
    scopes,
    Scope.SearchPreregistrationUsers,
    Application.Agent
  );
  const filteredLinesOfBusiness = !userHasScope(Scope.AgentSearchCustomerSupport)
    ? LinesOfBusinessCustomerSupport.filter(x => x.name === 'Agent')
    : LinesOfBusinessCustomerSupport;
  useEffect(() => {
    dispatch(searchCustomerUsers(undefined, customerSupport.filter, encryptedIdentifier, recruiterCodes));
  }, [dispatch, customerSupport.filter, encryptedIdentifier, recruiterCodes]);

  useEffect(() => {
    return () => {
      dispatch(changePreregisteredUsersFilter(EmptyPreregisteredUserFilter));
      dispatch(changeCustomerSupportFilter(EmptyCustomerSupportFilter));
    };
  }, [changePreregisteredUsersFilter, changeCustomerSupportFilter]);

  const hideResetConfirmation = () => {
    setResetPasswordConfirmation(O.none);
    dispatch(searchCustomerUsers(undefined, customerSupport.filter, encryptedIdentifier, recruiterCodes));
  };
  const hideDeleteConfirmation = () => {
    setDeleteConfirmation(O.none);
    dispatch(searchCustomerUsers(undefined, customerSupport.filter, encryptedIdentifier, recruiterCodes));
  };

  const getMenu = (user: UserDto) =>
    [
      scopes.includes(Scope.EditAnyAgent) &&
        (!scopes.includes(Scope.SearchAgentUsersByExternalExclusivityStatus) ||
          user.exclusivityStatus?.id === ExclusivityStatus.External) && (
          <MenuItem
            id={`edit-customer-support-btn-${user.userName}`}
            key="customer-support-edit"
            onClick={() => dispatch(navigateToEdit(user, encryptedTaxId))}
          >
            Edit
          </MenuItem>
        ),
      hasAccessToScopeByApplication(scopes, Scope.DeleteUser, Application.Agent) &&
        user.exclusivityStatus?.id === ExclusivityStatus.External && (
          <MenuItem
            id={`delete-customer-support-btn-${user.userName}`}
            key="customer-support-delete"
            onClick={() => {
              setDeleteConfirmation(O.some({user: user}));
            }}
          >
            Delete
          </MenuItem>
        ),
      scopes.includes(Scope.ResetUserWithTemporaryPass) &&
        user.exclusivityStatus?.id === ExclusivityStatus.External && (
          <MenuItem
            id={`reset-customer-support-btn-${user.userName}`}
            key="customer-support-reset"
            onClick={() =>
              setResetPasswordConfirmation(
                O.some({
                  userId: user.id!,
                  username: user.userName!,
                })
              )
            }
          >
            Reset Password
          </MenuItem>
        ),
    ].filter(Boolean) as JSX.Element[];

  const Header = (
    <>
      {hasAccessToScopeByApplication(scopes, Scope.AddPreregistrationUsers, Application.Agent) &&
        (isDesktop ? (
          <div className="agents__actions">
            <AddCustomerSupportMenu scopes={scopes} searchCustomerUsers={searchCustomerUsers} showModal={showModal} />
          </div>
        ) : (
          <>
            <AddCustomerSupportMobileMenu
              scopes={scopes}
              searchCustomerUsers={searchCustomerUsers}
              showModal={showModal}
            />
          </>
        ))}
    </>
  );

  const CustomerSupportAppBar = (
    <div className="commissions__tabs">
      <AppBar position="static">
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => {
            setCurrentTab(newValue);
          }}
        >
          <Tab key="customer-support-tab" label="Customer Support" />
          {hasAccessForPendingRegestration && <Tab key="pending-registrations-tab" label="Pending Registrations" />}
        </Tabs>
      </AppBar>
    </div>
  );

  const CustomerSupportAgentView = (
    <PagedView
      pagedViewKey="CustomerSupport"
      loadMore={page => dispatch(searchCustomerUsers(page, customerSupport.filter))}
      data={customerSupport.users}
      additionalFilterProperties={[
        {
          title: 'Agent Number',
          filter: withStringFilterProperties<CustomerSupportFilter>(filterProperties, 'agentNumber'),
        },
      ]}
      actions={Header}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Line of Business"
            filterProperties={withSelectFilterProperties(filterProperties, 'linesOfBusiness', filteredLinesOfBusiness)}
            default
          >
            {user => user.linesOfBusiness!.map(lineOfBusiness => lineOfBusiness.name!).join(', ')}
          </Column>
          <Column
            {...columnProps}
            title="User Name"
            filterProperties={withStringFilterProperties(filterProperties, 'userName')}
            default
            ddprivacy="mask"
          >
            {user => user.userName!}
          </Column>
          <Column
            {...columnProps}
            title="First Name"
            filterProperties={withStringFilterProperties(filterProperties, 'firstName')}
            default
            ddprivacy="mask"
          >
            {user => user.firstName!}
          </Column>
          <Column
            {...columnProps}
            title="Last Name"
            filterProperties={withStringFilterProperties(filterProperties, 'lastName')}
            default
          >
            {user => user.lastName!}
          </Column>
          <Column {...columnProps} title="Links">
            {user => user.links!.map(link => link.href!).join(',')}
          </Column>
          <Column {...columnProps} title="Exclusivity Status" default>
            {user => user.exclusivityStatus!.name!}
          </Column>
          <Column {...columnProps} title="Actions" isActions default>
            {user => (
              <>
                {hasAccessForCustomerSupportEmulate && (
                  <RequireAtLeastOneScope scopes={[Scope.AgentSearchCustomerSupport]}>
                    <Button
                      id={`customer-support-emulate-btn-${user.userName}`}
                      color="primary"
                      onClick={() => onImpersonate(user)}
                    >
                      Emulate
                    </Button>
                  </RequireAtLeastOneScope>
                )}
                <RequireAtLeastOneScope
                  scopes={[Scope.EditAnyAgent, Scope.UserDelete, Scope.ResetUserWithTemporaryPass]}
                >
                  {getMenu(user).length > 0 ? (
                    <ActionsMenu id={`customer-support-actions-menu-${user.userName}`}>{getMenu(user)}</ActionsMenu>
                  ) : (
                    <></>
                  )}
                </RequireAtLeastOneScope>
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );

  const renderCustomerSupportView = () => (
    <>
      {O.isSome(resetPassConfirmation) && (
        <ResetUserPassConfirmationModal
          open
          userId={resetPassConfirmation.value.userId!}
          username={resetPassConfirmation.value.username!}
          onClose={hideResetConfirmation}
        />
      )}
      {O.isSome(deleteConfirmation) && (
        <DeleteUserConfirmationModal open user={deleteConfirmation.value.user!} onClose={hideDeleteConfirmation} />
      )}
      {CustomerSupportAppBar}
      {CustomerSupportAgentView}
    </>
  );

  return currentTab === 0 ? (
    renderCustomerSupportView()
  ) : (
    <>
      {CustomerSupportAppBar}
      <RequireAtLeastOneScope scopes={[Scope.AgentSearchCustomerSupport]}>
        <PendingCustomerSupportRegistration />
      </RequireAtLeastOneScope>
    </>
  );
};
export default withUserClaims(CustomerSupport);
